import { useRef, useState } from "react";

import { uploads as uploadsApi } from "../api/resources";
import {
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Loading,
  Modal,
  PageHeader,
  Stat,
  StatusBadge,
} from "../components/ui";
import { useCurrentOrganization } from "../lib/OrganizationContext";
import { bytes, dateTime } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import type { UploadDetail } from "../lib/types";

export function UploadsPage() {
  const { organizationId, project } = useCurrentOrganization();
  const fileInput = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState("product_master");
  const [uploadedBy, setUploadedBy] = useState(
    () => localStorage.getItem("discovery.actor") ?? "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [detail, setDetail] = useState<UploadDetail | undefined>();

  const capabilities = useAsync(
    () =>
      organizationId
        ? uploadsApi.capabilities(organizationId)
        : Promise.resolve(undefined as never),
    [organizationId],
  );
  const list = useAsync(
    () =>
      organizationId
        ? uploadsApi.list(organizationId, { limit: 200 })
        : Promise.resolve({ items: [], total: 0, limit: 0, offset: 0 }),
    [organizationId],
  );

  if (!organizationId) {
    return (
      <Card>
        <EmptyState title="No organization selected" />
      </Card>
    );
  }

  async function upload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(undefined);
    try {
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        form.append("upload_category_code", category);
        if (uploadedBy) form.append("uploaded_by", uploadedBy);
        if (project) form.append("onboarding_project_id", project.id);
        await uploadsApi.create(organizationId!, form);
      }
      list.reload();
      if (fileInput.current) fileInput.current.value = "";
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  const rows = list.data?.items ?? [];
  const parsed = rows.filter(
    (row) => row.processing_status === "parsed",
  ).length;
  const failed = rows.filter((row) =>
    ["failed", "unsupported"].includes(row.processing_status),
  ).length;

  return (
    <>
      <PageHeader
        title="Uploads"
        description="Spreadsheets, SOPs, retailer guides and exports. The original file is stored permanently — parsing never replaces it."
      />

      <div className="stack">
        <ErrorBanner error={list.error ?? error} />

        <div className="grid grid--stats">
          <Stat label="Files" value={rows.length} />
          <Stat label="Parsed" value={parsed} tone="ok" />
          <Stat
            label="Not parsed"
            value={failed}
            tone={failed ? "warn" : undefined}
          />
          <Stat
            label="Max size"
            value={bytes(capabilities.data?.max_upload_bytes ?? 0)}
            hint={`${capabilities.data?.supported_extensions.length ?? 0} formats`}
          />
        </div>

        <Card title="Upload a source">
          <div className="grid grid--two">
            <Field
              label="Category"
              hint="Drives which extraction rules apply and whether rows are stored"
            >
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value)}
              >
                {(capabilities.data?.categories ?? []).map((row) => (
                  <option key={row.code} value={row.code}>
                    {row.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Uploaded by">
              <input
                value={uploadedBy}
                onChange={(event) => setUploadedBy(event.target.value)}
              />
            </Field>
          </div>
          <input
            ref={fileInput}
            type="file"
            multiple
            disabled={busy}
            onChange={(event) => void upload(event.target.files)}
          />
          <p className="small muted" style={{ marginBottom: 0 }}>
            Supported:{" "}
            {(capabilities.data?.supported_extensions ?? []).join(" ")}
          </p>
        </Card>

        <Card title="Uploaded sources" flush>
          {list.loading ? (
            <Loading />
          ) : rows.length === 0 ? (
            <EmptyState
              title="Nothing uploaded yet"
              description="A product master, a warehouse list and one SOP already tell the model a great deal."
            />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Category</th>
                    <th className="numeric">Size</th>
                    <th>Status</th>
                    <th>Uploaded</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div>{row.original_filename}</div>
                        <div className="tiny faint mono">
                          {(row.checksum_sha256 ?? "").slice(0, 12)}
                          {row.question_semantic_id
                            ? ` · ${row.question_semantic_id}`
                            : ""}
                        </div>
                      </td>
                      <td className="small muted">
                        {row.upload_category_code.replace(/_/g, " ")}
                      </td>
                      <td className="numeric">{bytes(row.size_bytes)}</td>
                      <td>
                        <StatusBadge status={row.processing_status} />
                        {row.processing_error ? (
                          <div
                            className="tiny"
                            style={{ color: "var(--danger)" }}
                          >
                            {row.processing_error}
                          </div>
                        ) : null}
                      </td>
                      <td className="small muted">
                        {dateTime(row.created_at)}
                      </td>
                      <td>
                        <div className="row" style={{ gap: 5 }}>
                          <button
                            className="btn--small"
                            onClick={async () => {
                              setDetail(
                                await uploadsApi.get(organizationId!, row.id),
                              );
                            }}
                          >
                            Inspect
                          </button>
                          <a
                            className="btn btn--small"
                            href={uploadsApi.downloadUrl(
                              organizationId!,
                              row.id,
                            )}
                          >
                            Download
                          </a>
                          <button
                            className="btn--small"
                            onClick={async () => {
                              await uploadsApi.reparse(organizationId!, row.id);
                              list.reload();
                            }}
                          >
                            Re-parse
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {detail ? (
        <Modal
          title={detail.original_filename}
          onClose={() => setDetail(undefined)}
        >
          <div className="row small muted" style={{ marginBottom: 14 }}>
            <StatusBadge status={detail.processing_status} />
            {detail.external_processing?.system ? (
              <span
                className={`badge badge--${detail.external_processing.status === "failed" ? "danger" : "neutral"}`}
                title="Deep processing (OCR, chunking, retrieval) happens in RAGFlow; the original file stays here."
                data-testid="handoff-badge"
              >
                {String(detail.external_processing.system)}:{" "}
                {String(detail.external_processing.status)}
              </span>
            ) : null}
            <span>{bytes(detail.size_bytes)}</span>
            <span>{detail.mime_type ?? "unknown type"}</span>
            <span>{detail.upload_category_code.replace(/_/g, " ")}</span>
          </div>

          {detail.sheets.length > 0 ? (
            <>
              <h3>Sheets</h3>
              {detail.sheets.map((sheet) => (
                <div key={sheet.id} style={{ marginBottom: 16 }}>
                  <strong>{sheet.sheet_name}</strong>{" "}
                  <span className="small muted">
                    {sheet.row_count} rows · {sheet.column_count} columns ·
                    header row {sheet.header_row_index ?? "—"}
                  </span>
                  <div className="table-wrap" style={{ marginTop: 6 }}>
                    <table>
                      <thead>
                        <tr>
                          {sheet.headers.map((header) => (
                            <th key={header}>
                              {header}
                              <div className="tiny faint">
                                {sheet.column_types[header]}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sheet.sample_rows.slice(0, 6).map((sample, index) => (
                          <tr key={index}>
                            {sheet.headers.map((header) => (
                              <td key={header} className="small truncate">
                                {String(
                                  (sample as Record<string, unknown>)[header] ??
                                    "—",
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </>
          ) : null}

          {detail.sections.length > 0 ? (
            <>
              <h3>Sections ({detail.sections.length})</h3>
              <div className="stack" style={{ gap: 8 }}>
                {detail.sections.slice(0, 12).map((section) => (
                  <div key={section.id} className="evidence">
                    <div className="evidence__ref">
                      {section.section_type}
                      {section.page_number
                        ? ` · page ${section.page_number}`
                        : ""}
                      {section.heading ? ` · ${section.heading}` : ""}
                    </div>
                    <div className="truncate" style={{ maxWidth: "100%" }}>
                      {section.text?.slice(0, 240) ?? "(no text)"}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {detail.sheets.length === 0 && detail.sections.length === 0 ? (
            <p className="muted">
              No parsed artifacts. The original file is stored and can be
              re-parsed at any time.
            </p>
          ) : null}
        </Modal>
      ) : null}
    </>
  );
}
